import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  IconCalendar,
  IconChevronLeft,
  IconChevronRight,
  IconChevronDown,
  IconCalendarEvent,
} from "@tabler/icons-react";
import { useLanguage } from "../context/LanguageContext";

interface DatePickerProps {
  dateValue?: string; // YYYY-MM-DD
  onChangeDate?: (dateStr: string) => void;
  dateError?: any;
  title?: string;
  className?: string;
  placeholder?: string;
  align?: "left" | "right";
  required?: boolean;
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const SHORT_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const DAYS_OF_WEEK = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

// Generate years 1900 - 2100 (All Years)
const ALL_YEARS = Array.from({ length: 201 }, (_, i) => 1900 + i);

// ── Custom Downward Opening Dropdown Component (10 visible items, thin scrollbar) ──
interface CustomSelectProps {
  value: number | string;
  options: { label: string; value: number | string }[];
  onChange: (val: any) => void;
  className?: string;
  buttonClassName?: string;
  menuClassName?: string;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  value,
  options,
  onChange,
  className = "",
  buttonClassName = "",
  menuClassName = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && listRef.current) {
      const selectedEl = listRef.current.querySelector("[data-selected='true']");
      if (selectedEl) {
        selectedEl.scrollIntoView({ block: "center" });
      }
    }
  }, [isOpen]);

  const selectedOption =
    options.find((opt) => String(opt.value) === String(value)) || options[0];

  return (
    <div className={`relative inline-block ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center justify-between gap-1 text-xs font-bold bg-yellow-50/80 border border-gray-200 rounded-lg px-2.5 py-1 outline-none hover:border-[#FBD037] cursor-pointer text-[#212121] transition-all w-full ${buttonClassName}`}
      >
        <span className="truncate">{selectedOption?.label}</span>
        <IconChevronDown size={14} className="text-gray-500 shrink-0" />
      </button>

      {isOpen && (
        <div
          ref={listRef}
          className={`absolute top-full left-0 mt-1 z-[250] min-w-full w-max max-h-[220px] overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-xl p-1 animate-in fade-in slide-in-from-top-1 [scrollbar-width:thin] [scrollbar-color:#FBD037_#FEF3C7] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:bg-[#FBD037] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-yellow-50 ${menuClassName}`}
        >
          {options.map((opt) => {
            const isSelected = String(opt.value) === String(value);
            return (
              <button
                key={String(opt.value)}
                type="button"
                data-selected={isSelected}
                onClick={() => {
                  onChange(opt.value);
                  setIsOpen(false);
                }}
                className={`w-full text-center whitespace-nowrap px-2.5 py-1.5 text-xs font-bold rounded-lg transition-all block ${
                  isSelected
                    ? "bg-[#FBD037] text-[#212121] shadow-xs"
                    : "hover:bg-yellow-100/70 text-[#212121]"
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const DatePicker: React.FC<DatePickerProps> = ({
  dateValue = "",
  onChangeDate,
  dateError,
  title,
  className = "",
  placeholder = "Select Date",
  align = "left",
  required = false,
}) => {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const [dropdownPosition, setDropdownPosition] = useState<{
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
  } | null>(null);

  const updateDropdownPosition = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const popoverHeight = 340;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const shouldOpenUpward = spaceBelow < popoverHeight && spaceAbove > 280;

    const pos: { top?: number; bottom?: number; left?: number; right?: number } = {};

    if (shouldOpenUpward) {
      pos.bottom = Math.max(10, window.innerHeight - rect.top + 6);
    } else {
      pos.top = Math.max(10, rect.bottom + 6);
    }

    if (align === "right") {
      pos.right = Math.max(10, window.innerWidth - rect.right);
    } else {
      pos.left = Math.max(10, rect.left);
    }

    setDropdownPosition(pos);
  }, [align]);

  const handleToggle = () => {
    if (!isOpen) {
      updateDropdownPosition();
    }
    setIsOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!isOpen) return;
    updateDropdownPosition();
    const handleUpdate = () => updateDropdownPosition();
    window.addEventListener("scroll", handleUpdate, true);
    window.addEventListener("resize", handleUpdate);
    return () => {
      window.removeEventListener("scroll", handleUpdate, true);
      window.removeEventListener("resize", handleUpdate);
    };
  }, [isOpen, updateDropdownPosition]);

  // Parse initial date or default to today
  const initialDateObj = dateValue ? new Date(dateValue) : new Date();
  const validInitialDate = isNaN(initialDateObj.getTime())
    ? new Date()
    : initialDateObj;

  const [currentYear, setCurrentYear] = useState<number>(
    validInitialDate.getFullYear(),
  );
  const [currentMonth, setCurrentMonth] = useState<number>(
    validInitialDate.getMonth(),
  );
  const [selectedDay, setSelectedDay] = useState<number>(
    validInitialDate.getDate(),
  );

  // Sync internal state when props change externally
  useEffect(() => {
    if (dateValue) {
      const d = new Date(dateValue);
      if (!isNaN(d.getTime())) {
        setCurrentYear(d.getFullYear());
        setCurrentMonth(d.getMonth());
        setSelectedDay(d.getDate());
      }
    }
  }, [dateValue]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node) &&
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Format Helper: YYYY-MM-DD
  const formatYYYYMMDD = (y: number, m: number, d: number) => {
    const mm = String(m + 1).padStart(2, "0");
    const dd = String(d).padStart(2, "0");
    return `${y}-${mm}-${dd}`;
  };

  // Format Display: e.g. "Aug 20, 2026"
  const formattedDateDisplay = dateValue
    ? `${SHORT_MONTHS[currentMonth]} ${String(selectedDay).padStart(2, "0")}, ${currentYear}`
    : "";

  const handleApply = (
    y = currentYear,
    m = currentMonth,
    d = selectedDay,
  ) => {
    const dateStr = formatYYYYMMDD(y, m, d);
    if (onChangeDate) onChangeDate(dateStr);
  };

  const handleDaySelect = (dayNum: number) => {
    setSelectedDay(dayNum);
    handleApply(currentYear, currentMonth, dayNum);
  };

  const handleToday = () => {
    const now = new Date();
    setCurrentYear(now.getFullYear());
    setCurrentMonth(now.getMonth());
    setSelectedDay(now.getDate());
    handleApply(now.getFullYear(), now.getMonth(), now.getDate());
  };

  // Calendar Calculation
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const firstDayIndex = new Date(currentYear, currentMonth, 1).getDay();
  const prevMonthDays = new Date(currentYear, currentMonth, 0).getDate();

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const isSelectedDate = (d: number) => {
    return selectedDay === d;
  };

  const isTodayDate = (d: number) => {
    const today = new Date();
    return (
      today.getFullYear() === currentYear &&
      today.getMonth() === currentMonth &&
      today.getDate() === d
    );
  };

  const labelText = title || placeholder;
  const isFloating = isOpen || Boolean(dateValue && dateValue !== "");

  return (
    <div className={`relative pt-1.5 w-full ${className}`} ref={containerRef}>
      {/* ── Main Input Trigger Bar ── */}
      <div
        onClick={handleToggle}
        className={`relative w-full rounded-xl pt-3.5 pb-2.5 px-3.5 outline-none text-xs bg-yellow-50 border transition-all duration-200 cursor-pointer flex items-center justify-between gap-2 text-[#1E1E1E] ${
          isOpen
            ? "border-[#FBD037] ring-2 ring-[#FBD037]/30"
            : dateError
            ? "border-red-500"
            : "border-[#FBD037]"
        }`}
      >
        <div className="flex items-center text-xs text-[#1E1E1E] whitespace-nowrap overflow-hidden">
          <span className={`truncate font-medium ${dateValue ? "text-[#1E293B]" : "text-gray-400"}`}>
            {dateValue ? formattedDateDisplay : (isFloating ? (placeholder ? t(placeholder) : "") : "")}
          </span>
        </div>

        <button
          type="button"
          className="text-[#212121] hover:text-black transition-colors p-0.5 rounded-lg shrink-0"
        >
          <IconCalendarEvent size={16} />
        </button>

        {/* Animated Floating Label */}
        {labelText && (
          <label
            className={`absolute pointer-events-none transition-all duration-200 ease-in-out select-none rounded-md ${
              isFloating
                ? "-top-2.5 left-2.5 text-[11px] font-semibold bg-yellow-50 z-10 px-1.5"
                : "left-3.5 top-1/2 -translate-y-1/2 text-xs bg-transparent z-0 px-1"
            } ${
              dateError
                ? "!text-red-500"
                : "!text-black font-medium"
            }`}
          >
            {t(labelText)} {required && <span className="text-red-500 ml-0.5">*</span>}
          </label>
        )}
      </div>

      {dateError && (
        <span className="text-red-500 text-xs block mt-1 font-light pl-1">
          {dateError?.message || "Date is required"}
        </span>
      )}

      {/* ── Floating Date-Only Picker Dropdown ── */}
      {isOpen && dropdownPosition && createPortal(
        <div
          ref={popoverRef}
          style={{
            position: "fixed",
            top: dropdownPosition.top,
            bottom: dropdownPosition.bottom,
            left: dropdownPosition.left,
            right: dropdownPosition.right,
          }}
          className="z-[99999] bg-white rounded-2xl border border-gray-100 shadow-[0_20px_50px_rgba(0,0,0,0.25)] p-3.5 w-[260px] animate-in fade-in select-none"
        >
          {/* ── Top Header: Navigation & Selectors ── */}
          <div className="flex items-center justify-between pb-2 mb-2 border-b border-gray-100 w-full">
            <button
              type="button"
              onClick={handlePrevMonth}
              className="p-1 rounded-full text-gray-600 hover:bg-yellow-100/80 hover:text-black transition-all active:scale-95 shrink-0"
            >
              <IconChevronLeft size={16} />
            </button>

            <div className="flex items-center gap-1 justify-center">
              <CustomSelect
                value={currentMonth}
                options={MONTH_NAMES.map((name, idx) => ({
                  label: name,
                  value: idx,
                }))}
                onChange={(val) => setCurrentMonth(Number(val))}
                className="min-w-[95px] sm:min-w-[105px]"
                buttonClassName="py-0.5 px-2 text-xs font-bold text-[#212121] bg-white border-gray-200 hover:border-[#FBD037]"
                menuClassName="min-w-[105px]"
              />

              <CustomSelect
                value={currentYear}
                options={ALL_YEARS.map((yr) => ({
                  label: String(yr),
                  value: yr,
                }))}
                onChange={(val) => setCurrentYear(Number(val))}
                className="min-w-[62px]"
                buttonClassName="py-0.5 px-1.5 text-xs font-bold text-[#212121] bg-white border-gray-200 hover:border-[#FBD037]"
                menuClassName="min-w-[68px]"
              />
            </div>

            <button
              type="button"
              onClick={handleNextMonth}
              className="p-1 rounded-full text-gray-600 hover:bg-yellow-100/80 hover:text-black transition-all active:scale-95 shrink-0"
            >
              <IconChevronRight size={16} />
            </button>
          </div>

          {/* ── Calendar Grid ── */}
          <div>
            {/* Days of Week Header */}
            <div className="grid grid-cols-7 text-center mb-1">
              {DAYS_OF_WEEK.map((d) => (
                <span
                  key={d}
                  className="text-[10px] font-bold text-gray-400 uppercase"
                >
                  {d}
                </span>
              ))}
            </div>

            {/* Days Grid */}
            <div className="grid grid-cols-7 gap-1 text-center">
              {/* Overflow Previous Month Days */}
              {Array.from({ length: firstDayIndex }).map((_, idx) => (
                <div
                  key={`prev-${idx}`}
                  className="aspect-square flex items-center justify-center text-xs text-gray-300 font-light"
                >
                  {prevMonthDays - firstDayIndex + idx + 1}
                </div>
              ))}

              {/* Current Month Days */}
              {Array.from({ length: daysInMonth }).map((_, idx) => {
                const dayNum = idx + 1;
                const selected = isSelectedDate(dayNum);
                const today = isTodayDate(dayNum);

                return (
                  <button
                    key={dayNum}
                    type="button"
                    onClick={() => handleDaySelect(dayNum)}
                    className={`aspect-square flex items-center justify-center text-xs rounded-lg font-bold transition-all duration-200 ${
                      selected
                        ? "bg-[#FBD037] text-[#212121] shadow-md shadow-amber-200/60 scale-105"
                        : today
                        ? "border border-[#FBD037] text-slate-900 bg-yellow-50"
                        : "hover:bg-yellow-100/70 text-[#212121]"
                    }`}
                  >
                    {dayNum}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Bottom Action Bar ── */}
          <div className="mt-3 pt-2.5 border-t border-gray-100 flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={handleToday}
              className="text-xs font-bold text-[#212121] bg-gray-100/80 hover:bg-yellow-100 border border-transparent py-1.5 px-4 rounded-xl transition-all text-center cursor-pointer"
            >
              Today
            </button>

            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="bg-[#FBD037] hover:bg-yellow-400 text-[#212121] font-extrabold text-xs py-1.5 px-6 rounded-xl transition-all shadow-md shadow-amber-200/60 active:scale-95 text-center cursor-pointer"
            >
              Done
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default DatePicker;
